import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronRight, HardDrive, Loader2 } from "lucide-react";
import {
  DriveEntriesQuery,
  DriveEntry,
  VaultSyncProviderId,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DriveBrowserError } from "../errors";
import { VAULT_SYNC_PROVIDER_LABELS } from "../vaultSyncsStore";
import { fetchEntries } from "./driveBrowserApi";
import { DriveEntryList } from "./DriveEntryList";

/** Item id that syncs a whole drive rather than one folder. */
const DRIVE_ROOT_ITEM_ID = "root";

/** The upstream folder (or whole drive) picked for syncing. */
export interface DriveSelection {
  driveId: string;
  itemId: string;
  location: string;
}

interface DriveBrowserProps {
  connectionId: string;
  providerId: VaultSyncProviderId;
  selection: DriveSelection | null;
  onSelect: (selection: DriveSelection | null) => void;
  onReconnectRequired: () => void;
}

/**
 * Folder picker for a cloud drive connection: drills through sites, libraries,
 * and folders.
 * The folder being viewed is the selection; a drive's top level offers "Sync entire drive".
 * @param props - The connection to browse, the current selection, and selection callbacks
 * @returns The drive browser element
 */
export function DriveBrowser({
  connectionId,
  providerId,
  selection,
  onSelect,
  onReconnectRequired,
}: DriveBrowserProps) {
  const [path, setPath] = useState<DriveEntry[]>([]);
  const [entries, setEntries] = useState<DriveEntry[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onReconnectRequiredRef = useRef(onReconnectRequired);

  const current = path.length > 0 ? path[path.length - 1] : null;
  const query = useMemo(() => buildEntriesQuery(current), [current]);
  const queryRef = useRef(query);

  useEffect(() => {
    onReconnectRequiredRef.current = onReconnectRequired;
  }, [onReconnectRequired]);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    let isStale = false;
    setIsLoading(true);
    setError(null);
    setEntries([]);
    setNextPageToken(null);

    fetchEntries(connectionId, query)
      .then((page) => {
        if (isStale) {
          return;
        }
        setEntries(page.entries);
        setNextPageToken(page.nextPageToken);
      })
      .catch((failure: unknown) => {
        if (!isStale) {
          setError(describeFailure(failure, onReconnectRequiredRef.current));
        }
      })
      .finally(() => {
        if (!isStale) {
          setIsLoading(false);
        }
      });

    return () => {
      isStale = true;
    };
  }, [connectionId, query]);

  const navigateTo = (nextPath: DriveEntry[]) => {
    setPath(nextPath);
    onSelect(toFolderSelection(nextPath));
  };

  const loadMore = async () => {
    if (!nextPageToken) {
      return;
    }
    const pageQuery = query;
    setIsLoadingMore(true);
    try {
      const page = await fetchEntries(connectionId, {
        ...pageQuery,
        pageToken: nextPageToken,
      });
      if (queryRef.current !== pageQuery) {
        return;
      }
      setEntries((previous) => [...previous, ...page.entries]);
      setNextPageToken(page.nextPageToken);
    } catch (failure) {
      setError(describeFailure(failure, onReconnectRequiredRef.current));
    } finally {
      setIsLoadingMore(false);
    }
  };

  const crumbs = [
    { key: "root", label: describeRootLabel(providerId), path: [] },
    ...path.map((entry, index) => ({
      key: entry.id,
      label: entry.name,
      path: path.slice(0, index + 1),
    })),
  ];

  const isDriveRootSelected =
    current?.kind === "drive" &&
    selection?.driveId === current.id &&
    selection.itemId === DRIVE_ROOT_ITEM_ID;

  return (
    <div className="space-y-3">
      <nav
        aria-label="Folder path"
        className="-mx-1 flex flex-wrap items-center gap-1 text-sm"
      >
        {crumbs.map((crumb, index) => {
          const isCurrent = index === crumbs.length - 1;
          return (
            <span key={crumb.key} className="flex min-w-0 items-center gap-1">
              {index > 0 && (
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              )}
              {isCurrent ? (
                <span
                  aria-current="page"
                  className="break-words px-1 font-medium"
                >
                  {crumb.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => navigateTo(crumb.path)}
                  className="break-words rounded px-1 text-left text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {crumb.label}
                </button>
              )}
            </span>
          );
        })}
      </nav>

      <div className="max-h-72 overflow-y-auto rounded-md border">
        {current?.kind === "drive" && (
          <button
            type="button"
            aria-pressed={isDriveRootSelected}
            onClick={() =>
              onSelect({
                driveId: current.id,
                itemId: DRIVE_ROOT_ITEM_ID,
                location: describeLocation(path),
              })
            }
            className={cn(
              "flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
              isDriveRootSelected && "bg-muted",
            )}
          >
            <HardDrive className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">Sync everything in {current.name}</span>
            {isDriveRootSelected && (
              <Check className="size-4 shrink-0 text-primary duration-150 motion-safe:animate-in motion-safe:zoom-in-50" />
            )}
          </button>
        )}
        <DriveEntryList
          entries={entries}
          isLoading={isLoading}
          error={error}
          emptyLabel={current ? "No folders here" : "Nothing found"}
          onOpen={(entry) => navigateTo([...path, entry])}
        />
        {nextPageToken && !isLoading && (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={isLoadingMore}
              onClick={loadMore}
            >
              Load more
              {isLoadingMore && <Loader2 className="size-4 animate-spin" />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Names the root breadcrumb: SharePoint's top level lists sites, every other provider's lists its drive.
 * @param providerId - The drive provider being browsed
 * @returns The root breadcrumb label
 */
function describeRootLabel(providerId: VaultSyncProviderId): string {
  if (providerId === "sharepoint") {
    return "Sites";
  }
  return VAULT_SYNC_PROVIDER_LABELS[providerId];
}

/**
 * Builds the entries query that lists the children of the entry being viewed.
 * @param current - The entry being viewed, or null for the roots
 * @returns The query for the entries route
 */
function buildEntriesQuery(current: DriveEntry | null): DriveEntriesQuery {
  if (!current) {
    return {};
  }
  if (current.kind === "site") {
    return { parentKind: "site", parentId: current.id };
  }
  if (current.kind === "drive") {
    return { parentKind: "drive", driveId: current.id };
  }
  return {
    parentKind: "folder",
    driveId: current.driveId ?? "",
    parentId: current.id,
  };
}

/**
 * Selects the viewed entry when it is a folder; sites and drives are not syncable targets.
 * @param path - The entries from the root down to the one being viewed
 * @returns The folder selection, or null
 */
function toFolderSelection(path: DriveEntry[]): DriveSelection | null {
  const entry = path[path.length - 1];
  if (entry?.kind !== "folder" || !entry.driveId) {
    return null;
  }
  return {
    driveId: entry.driveId,
    itemId: entry.id,
    location: describeLocation(path),
  };
}

/**
 * Joins a browse path into a readable location such as "Sales › Documents › Quotes".
 * @param path - The entries from the root down to the selected one
 * @returns The location label
 */
function describeLocation(path: DriveEntry[]): string {
  return path.map((entry) => entry.name).join(" › ");
}

/**
 * Turns a failed browse request into a message, flagging a rejected connection for reconnect.
 * @param failure - The thrown error
 * @param onReconnectRequired - Called when the drive provider rejected the connection
 * @returns The message to show
 */
function describeFailure(
  failure: unknown,
  onReconnectRequired: () => void,
): string {
  if (!(failure instanceof DriveBrowserError)) {
    return "Failed to load folders";
  }
  if (failure.isReconnectRequired) {
    onReconnectRequired();
  }
  return failure.message;
}
